import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TypedThunk } from "..";
import { saveState } from "../../utils/localStorage.utils";
import { LocalStorageKeys } from "../../utils/strings.utils";
import api, {
  API_ALCHEMY_API,
  API_ALCHEMY_COLLECTION_API,
} from "../../utils/api";
import { getSecrets } from "../../config";
import { ethers } from "ethers";
import { decodeImageSrc } from "../../utils/format.utils";
import { NFTIcon } from "../../images";

const { ENS_ADDRESS, OPENSEA_LINK, OPENSEA_LINK_MATIC, BACKEND_URL } =
  getSecrets();

type Asset = {
  collections: { [address: string]: Collection[] };
  assets: { [address: string]: asset };
  isFetchingCollection: boolean;
  isFetchingAsset: boolean;
  ensNames: { [address: string]: string };
  collectionNextPageKey: { [address: string]: string };
  assetnNextPageKey: { [address: string]: string };
};

export interface Collection {
  name: string;
  slug: string;
  imageUrl: string;
}

export interface asset {
  [slug: string]: assetContent[];
}

export interface assetContent {
  imageUrl: string;
  permalink: string;
  name: string;
  tokenId: any;
  assetContract: string;
  itemType: any;
}

const initialState: Asset = {
  collections: {},
  assets: {},
  isFetchingCollection: true,
  isFetchingAsset: false,
  ensNames: {},
  collectionNextPageKey: {},
  assetnNextPageKey: {},
};

export const assetSlice = createSlice({
  name: "asset",
  initialState,
  reducers: {
    setCollections: (
      state,
      action: PayloadAction<{ [address: string]: Collection[] }>
    ) => {
      state.collections = action.payload;
    },
    setAssets: (state, action: PayloadAction<{ [address: string]: asset }>) => {
      state.assets = action.payload;
      saveState(LocalStorageKeys.ASSET_LIST, action.payload);
    },
    setIsFetchingCollection: (state, action: PayloadAction<boolean>) => {
      state.isFetchingCollection = action.payload;
    },
    setIsFetchingAsset: (state, action: PayloadAction<boolean>) => {
      state.isFetchingAsset = action.payload;
    },
    setEnsNames: (
      state,
      action: PayloadAction<{ [address: string]: string }>
    ) => {
      state.ensNames = action.payload;
    },
    setCollectionNextPageKey: (
      state,
      action: PayloadAction<{ [address: string]: string }>
    ) => {
      state.collectionNextPageKey = action.payload;
    },
    setAssetNextPageKey: (
      state,
      action: PayloadAction<{ [address: string]: string }>
    ) => {
      state.assetnNextPageKey = action.payload;
    },
  },
});

export const {
  setCollections,
  setAssets,
  setIsFetchingCollection,
  setIsFetchingAsset,
  setEnsNames,
  setCollectionNextPageKey,
  setAssetNextPageKey,
} = assetSlice.actions;

export default assetSlice.reducer;

const getImageUrl = (url: string) => {
  if (url?.substring(0, 7) == "ipfs://") {
    return "https://alchemy.mypinata.cloud/ipfs/" + url.substring(7);
  } else {
    if (
      url?.substring(0, 18) == "https://i.seadn.io" ||
      url?.substring(0, 42) == "https://d1q39w3eq2bx7b.cloudfront.net" ||
      url?.substring(0, 35) == "https://alchemy.mypinata.cloud/ipfs" ||
      url?.substring(0, 33) == "https://lh3.googleusercontent.com" ||
      url?.substring(0, 27) == "https://openseauserdata.com"
    ) {
      return url;
    } else {
      if (url.includes("data:image/svg+xml;base64")) {
        return url;
      } else if (url.includes("data:image/png;base64")) {
        return url;
      } else {
        return NFTIcon;
      }
    }
  }
};

const getAllCollections = async (
  assets: any[],
  address: string,
  page: string,
  chain: string
): Promise<any> =>
  new Promise(async (resolve) => {
    let res;

    try {
      res = await api.get(API_ALCHEMY_COLLECTION_API, {
        params: {
          wallet: address,
          chain: chain,
          pageKey: page,
        },
      });
    } catch (e) {
      console.log(e);
      return;
    }

    const data = assets.concat(
      res?.data?.contracts
        ?.filter((a: any) => a.opensea.imageUrl)
        ?.map((a: any) => ({
          name: a.name,
          slug: a.address,
          imageUrl: decodeImageSrc(getImageUrl(a.opensea.imageUrl)),
        }))
    );
    // if (chain == "ethereum") {
    //   resolve(getAllCollections(data, address, "polygon"));
    // } else {
    resolve({ data: data, pageKey: res?.data?.pageKey });
    // }
  });

export const getCollections =
  (queryAddress?: string, page?: string): TypedThunk =>
  async (dispatch, getState) => {
    if (!queryAddress && !getState().auth.currentAddress) return;
    const address = queryAddress ?? getState().auth.currentAddress;
    if (page == undefined) {
      dispatch(setIsFetchingCollection(true));
    }
    if (page == "") {
      return;
    }

    let res = await getAllCollections([], address, page, "ethereum");
    try {
      if (res.pageKey != undefined) {
        dispatch(setCollectionNextPageKey({ [address]: res.pageKey }));
      } else {
        dispatch(setCollectionNextPageKey({ [address]: "" }));
      }
      res = res.data.filter(
        (obj: any, index: any) =>
          res?.data.findIndex((item: any) => item?.slug === obj?.slug) === index
      );

      let a = getState().asset.collections[address];
      if (a == undefined) {
        a = [];
      }
      a = a.concat(
        res?.map((d: any) => ({
          name: d.name,
          slug: d.slug,
          imageUrl: decodeImageSrc(d.imageUrl),
        }))
      );
      const uniqueObjects = Array.from(
        new Set(a.map((obj) => JSON.stringify(obj)))
      ).map((stringified) => JSON.parse(stringified));
      dispatch(
        setCollections({
          ...getState().asset.collections,
          [address]: uniqueObjects,
        })
      );
    } catch (e) {
      console.log(e);
    }

    dispatch(setIsFetchingCollection(false));
  };

const getAllAssets = async (
  assets: any[],
  collection: string,
  address: string,
  page: string,
  chain: string
): Promise<any> =>
  new Promise(async (resolve) => {
    let res;
    let opensea_link = OPENSEA_LINK;
    if (chain == "polygon") {
      opensea_link = OPENSEA_LINK_MATIC;
    }
    try {
      res = await api.get(API_ALCHEMY_API, {
        params: {
          wallet: address,
          contractAddresses: collection,
          pageKey: page,
          chain: chain,
        },
      });
    } catch (e) {
      console.log(e);
      return;
    }
    const data = assets.concat(
      res?.data?.ownedNfts
        ?.filter((a: any) => a.metadata.image)
        ?.map((a: any) => ({
          imageUrl: decodeImageSrc(getImageUrl(a.metadata.image)),
          permalink:
            opensea_link +
            a.contract.address +
            "/" +
            ethers.BigNumber.from(a.id.tokenId).toString(),
          name: a.metadata.name,
          tokenId: ethers.BigNumber.from(a.id.tokenId).toString(),
          assetContract: a.contract.address,
          itemType: a.contractMetadata.tokenType,
        }))
    );
    // if (chain == "ethereum")
    //   resolve(getAllAssets(data, collection, address, "polygon"));
    // else
    resolve({ data: data, pageKey: res?.data?.pageKey });
  });

export const getAssets =
  (collection: string, queryAddress?: string, page?: string): TypedThunk =>
  async (dispatch, getState) => {
    if (!queryAddress && !getState().auth.currentAddress) return;
    const address = queryAddress ?? getState().auth.currentAddress;
    dispatch(setIsFetchingAsset(true));
    if (collection === "ens") {
      const res = await getAllAssets(
        [],
        ENS_ADDRESS,
        address,
        page,
        "ethereum"
      );
      dispatch(
        setAssets({
          ...getState().asset.assets,
          [address]: {
            ...getState().asset.assets[address],
            [collection]: res.data,
          },
        })
      );
      dispatch(setIsFetchingAsset(false));
    } else {
      const res = await getAllAssets([], collection, address, page, "ethereum");
      if (res.pageKey != undefined) {
        dispatch(setCollectionNextPageKey({ [address]: res.pageKey }));
      } else {
        dispatch(setCollectionNextPageKey({ [address]: "" }));
      }
      let a: any[];
      if (
        getState().asset.assets[address] &&
        getState().asset.assets[address][collection]
      ) {
        a = getState().asset.assets[address][collection];
      } else {
        a = [];
      }
      a = a.concat(res.data);
      const uniqueObjects = Array.from(
        new Set(a.map((obj) => JSON.stringify(obj)))
      ).map((stringified) => JSON.parse(stringified));

      dispatch(
        setAssets({
          ...getState().asset.assets,
          [address]: {
            ...getState().asset.assets[address],
            [collection]: uniqueObjects,
          },
        })
      );
      dispatch(setIsFetchingAsset(false));
    }
  };
